import { useState } from "react";
import { toast } from "sonner";
import { CircleDollarSign, Loader2 } from "lucide-react";
import { useCurrency, setDisplayCurrency } from "@/utils/currency";

/**
 * Owner-only display currency. Display preference ONLY — every value in the
 * database stays in USD; AUD amounts are converted at render time using the
 * cached live rate. Workers and managers always keep seeing USD.
 */
export const CurrencyCard = () => {
  const { currency, rate, rateUpdatedAt } = useCurrency();
  const [saving, setSaving] = useState(false);

  const pick = async (c) => {
    if (saving || c === currency) return;
    setSaving(true);
    try {
      const s = await setDisplayCurrency(c);
      toast.success(
        c === "AUD"
          ? `Now displaying AUD — 1 USD = ${s.rate} AUD. Stored values are untouched (still USD).`
          : "Now displaying USD."
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not switch currency right now.");
    } finally {
      setSaving(false);
    }
  };

  const btn = (c, label, sub) => (
    <button
      key={c}
      data-testid={`currency-select-${c.toLowerCase()}`}
      onClick={() => pick(c)}
      disabled={saving}
      className={`flex-1 rounded-2xl border px-4 py-3 text-left transition-colors disabled:opacity-60 ${
        currency === c
          ? "border-cyan-500/50 bg-cyan-500/10"
          : "border-[var(--pv-border)] bg-[var(--pv-subtle)] hover:border-cyan-500/30"
      }`}
    >
      <span className={`flex items-center gap-1.5 text-sm font-bold ${currency === c ? "text-cyan-300" : "text-[var(--pv-text-2)]"}`}>
        {label} {saving && currency !== c && <Loader2 className="h-3 w-3 animate-spin" />}
      </span>
      <span className="mt-0.5 block text-[11px] text-[var(--pv-text-4)]">{sub}</span>
    </button>
  );

  return (
    <div data-testid="currency-card" className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
      <div className="flex items-center gap-2.5">
        <CircleDollarSign className="h-4.5 w-4.5 text-cyan-400" />
        <h2 className="font-display text-base font-bold text-[var(--pv-text)]">Display currency</h2>
        <span className="rounded-full border border-[var(--pv-border)] px-2 py-0.5 text-[10px] font-bold uppercase text-[var(--pv-text-4)]">Owner only</span>
      </div>
      <p className="mt-1.5 text-xs text-[var(--pv-text-4)]">
        Changes how money is shown to <span className="font-semibold text-[var(--pv-text-3)]">you</span> across the whole admin console.
        Everything is stored in USD and stays in USD — this is display-only, and workers &amp; managers keep seeing USD.
      </p>
      <div className="mt-4 flex gap-3">
        {btn("USD", "USD — US Dollar", "Show amounts exactly as stored")}
        {btn("AUD", "AUD — Australian Dollar", "Convert for display at the live rate")}
      </div>
      {currency === "AUD" && rate && (
        <p data-testid="currency-rate-note" className="mt-3 text-[11px] font-medium text-[var(--pv-text-4)]">
          1 USD = {rate} AUD{rateUpdatedAt ? ` · rate updated ${new Date(rateUpdatedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""} · display only, stored values unchanged
        </p>
      )}
    </div>
  );
};
